// Thin JSON client for /api. Every call sends the X-Agile-Vault header the
// server requires on mutating routes (a CSRF guard).

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let onUnauthorised: (() => void) | null = null;
/** Called when a signed-in call comes back 401 (session ended) — the app locks. */
export const setUnauthorisedHandler = (fn: (() => void) | null) => {
  onUnauthorised = fn;
};

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; raw?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = { "X-Agile-Vault": "1" };
  let body: string | undefined;
  if (init.raw !== undefined) {
    headers["Content-Type"] = "text/plain";
    body = init.raw;
  } else if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.body);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: init.method ?? (body !== undefined ? "POST" : "GET"),
      headers,
      body,
      credentials: "same-origin",
    });
  } catch {
    throw new ApiError("Can't reach Agile Vault. Check your connection and try again.", 0);
  }
  if (res.status === 401 && onUnauthorised) onUnauthorised();
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error ?? `Request failed (${res.status}).`;
    throw new ApiError(msg, res.status);
  }
  return data as T;
}

export const errMsg = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong. Try again.");
